/**
 * ============================================================
 * KEJJA OS — src/components/NotificationBell.tsx
 * Drop into the top bar. Shows unread count + dropdown with recent.
 * ============================================================
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, X } from 'lucide-react';
import {
  useUnreadCount, useNotifications, useMarkRead, useMarkAllRead, useDismissNotification,
} from '@/hooks/useNotifications';
import type { Notification } from '@/lib/notificationsApi';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  const { data: countData } = useUnreadCount();
  const { data: listData } = useNotifications('all');

  const unreadCount = countData?.count ?? 0;
  const recent = (listData?.notifications ?? []).slice(0, 10);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node)
        && buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        className="relative h-9 w-9 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground flex items-center justify-center transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold leading-none flex items-center justify-center tabular-nums">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={popoverRef}
          className="absolute right-0 top-full mt-2 z-50 w-[360px] max-w-[calc(100vw-1rem)] rounded-lg border border-border bg-popover shadow-xl"
        >
          <NotificationDropdown
            notifications={recent}
            unreadCount={unreadCount}
            onClose={() => setOpen(false)}
            onOpenAll={() => { setOpen(false); navigate('/notifications'); }}
          />
        </div>
      )}
    </div>
  );
}

// ============================================================
// Dropdown contents
// ============================================================

function NotificationDropdown({
  notifications, unreadCount, onClose, onOpenAll,
}: {
  notifications: Notification[];
  unreadCount: number;
  onClose: () => void;
  onOpenAll: () => void;
}) {
  const markAllMut = useMarkAllRead();

  return (
    <div className="flex flex-col max-h-[min(70vh,520px)]">
      <div className="p-3 flex items-center justify-between border-b border-border">
        <div>
          <h3 className="text-sm font-bold">Notifications</h3>
          {unreadCount > 0 && (
            <p className="text-[10px] text-muted-foreground">{unreadCount} unread</p>
          )}
        </div>
        <div className="flex gap-1">
          {unreadCount > 0 && (
            <Button
              size="sm" variant="ghost" className="h-7 text-[10px]"
              onClick={() => markAllMut.mutate()}
            >
              <Check className="w-3 h-3 mr-0.5" /> Mark all read
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="p-8 text-center text-xs text-muted-foreground">
            <Bell className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
            You're all caught up.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {notifications.map((n) => (
              <NotificationRow key={n.id} notification={n} onAfterAction={onClose} />
            ))}
          </div>
        )}
      </div>

      <div className="p-2 border-t border-border">
        <Button
          variant="ghost" size="sm" className="w-full text-xs"
          onClick={onOpenAll}
        >
          View all notifications
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// Row
// ============================================================

function NotificationRow({ notification, onAfterAction }: {
  notification: Notification; onAfterAction?: () => void;
}) {
  const markReadMut = useMarkRead();
  const dismissMut = useDismissNotification();
  const navigate = useNavigate();
  const isUnread = notification.state === 'delivered' || notification.state === 'pending';

  const handleClick = () => {
    if (isUnread) markReadMut.mutate(notification.id);
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onAfterAction?.();
    }
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'p-3 flex items-start gap-2.5 cursor-pointer hover:bg-muted/50 transition-colors',
        isUnread && 'bg-sky-50/60 dark:bg-sky-950/10',
      )}
    >
      <div className={cn(
        'h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0 text-sm',
        notification.priority === 'critical' ? 'bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300'
          : notification.priority === 'quiet' ? 'bg-muted text-muted-foreground'
          : 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300',
      )}>
        {notification.icon ?? '🔔'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <span className={cn(
            'text-xs flex-1',
            isUnread ? 'font-semibold text-foreground' : 'text-muted-foreground',
          )}>
            {notification.title}
          </span>
          {isUnread && (
            <span className="h-2 w-2 rounded-full bg-sky-500 flex-shrink-0 mt-1"></span>
          )}
        </div>
        {notification.body && (
          <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{notification.body}</p>
        )}
        <p className="text-[9px] text-muted-foreground mt-1 uppercase tracking-wider">
          {notification.module} · {timeAgo(notification.createdAt)}
        </p>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          dismissMut.mutate(notification.id);
        }}
        className="flex-shrink-0 h-6 w-6 rounded hover:bg-muted text-muted-foreground flex items-center justify-center opacity-0 group-hover:opacity-100"
        aria-label="Dismiss"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

function timeAgo(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffSec = Math.floor((now - then) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-KE', { month: 'short', day: 'numeric' });
}
